import React, { useCallback, useEffect, useRef, useState } from 'react';

import Icon from 'ds/Icon';
import { Layout, Text } from 'ds';

import { ReactComponent as CodiSlackMark } from 'ux/Layouts/Shared/Navigation/Codi-C-Logo.svg';

export interface ConvoSlideData {
  id: string;
  messages: ReadonlyArray<{
    variant: 'user1' | 'user2' | 'codi';
    initial: string;
    name: string;
    time: string;
    showAppTag?: boolean;
    body: React.ReactNode;
  }>;
}

export const HERO_CONVO_SLIDES: ReadonlyArray<ConvoSlideData> = [
  {
    id: '0',
    messages: [
      {
        variant: 'user1',
        initial: 'E',
        name: 'Emmalee',
        time: '9:04 AM',
        body: 'we ran out of Lacroix'
      },
      {
        variant: 'codi',
        initial: 'C',
        name: 'Codi',
        time: '9:04 AM',
        showAppTag: true,
        body: (
          <>
            Got it. Added 4 boxes of Lacroix to your next pantry restocking.
            <span className="HomeV10-msgChip">
              <Icon name="checkmark" size="xs" color="blue-600" stroke={2} />
              Added to next order
            </span>
          </>
        )
      }
    ]
  },
  {
    id: '1',
    messages: [
      {
        variant: 'user2',
        initial: 'J',
        name: 'Jordan',
        time: '2:15 PM',
        body: "there's a leak in the kitchen"
      },
      {
        variant: 'codi',
        initial: 'C',
        name: 'Codi',
        time: '2:15 PM',
        showAppTag: true,
        body: (
          <>
            On it — plumber scheduled for tomorrow afternoon.
            <span className="HomeV10-msgChip">
              <Icon name="checkmark" size="xs" color="blue-600" stroke={2} />
              Plumber · Tomorrow 1–3 PM
            </span>
          </>
        )
      }
    ]
  },
  {
    id: '2',
    messages: [
      {
        variant: 'user2',
        initial: 'J',
        name: 'Jordan',
        time: '3:20 PM',
        body: 'We need AV conferencing equipment for our 3 meeting rooms.'
      },
      {
        variant: 'codi',
        initial: 'C',
        name: 'Codi',
        time: '3:21 PM',
        showAppTag: true,
        body: (
          <>
            Sure thing. Anthony, our IT consultant, will come onsite this Thursday and recommend the right equipment for
            you.
            <span className="HomeV10-msgChip">
              <Icon name="checkmark" size="xs" color="blue-600" stroke={2} />
              Thursday confirmed
            </span>
          </>
        )
      }
    ]
  },
  {
    id: '3',
    messages: [
      {
        variant: 'user1',
        initial: 'E',
        name: 'Emmalee',
        time: '4:05 PM',
        body: 'we have a big event next Thursday. Can you schedule a deep cleaning after 10pm?'
      },
      {
        variant: 'codi',
        initial: 'C',
        name: 'Codi',
        time: '4:05 PM',
        showAppTag: true,
        body: (
          <>
            {"Absolutely, it's done."}
            <span className="HomeV10-msgChip">
              <Icon name="checkmark" size="xs" color="blue-600" stroke={2} />
              Deep clean · Thu after 10 PM
            </span>
          </>
        )
      }
    ]
  },
  {
    id: '4',
    messages: [
      {
        variant: 'codi',
        initial: 'C',
        name: 'Codi',
        time: '4:00 PM',
        showAppTag: true,
        body: (
          <div className="HomeV10-msgNotif">
            <Text size="tag" color="blue-600" bold __style={{ display: 'block', marginBottom: 4 }}>
              UPDATE
            </Text>
            Your 5 standing desks are expected to be delivered next Tuesday between 10 AM–12 PM.
          </div>
        )
      }
    ]
  }
];

interface Props {
  slides: ReadonlyArray<ConvoSlideData>;
}

const SLIDE_INTERVAL_MS = 5000;

const HeroConvoSlides: React.FC<Props> = ({ slides }) => {
  const [active, setActive] = useState(0);
  const [slideKey, setSlideKey] = useState(0);
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = window.setInterval(() => {
      setActive(cur => {
        const next = (cur + 1) % slides.length;
        setSlideKey(k => k + 1);
        return next;
      });
    }, SLIDE_INTERVAL_MS);
  }, [slides.length]);

  const goTo = useCallback(
    (i: number) => {
      setActive(i);
      setSlideKey(k => k + 1);
      startTimer();
    },
    [startTimer]
  );

  useEffect(() => {
    startTimer();
    return () => clearTimer();
  }, [startTimer]);

  return (
    <>
      <div className="HomeV10-convoArea">
        {slides.map((slide, slideIdx) => (
          <div
            key={slide.id}
            className={`HomeV10-convoSlide${slideIdx === active ? ' is-active' : ''}`}
            aria-hidden={slideIdx === active ? false : true}
          >
            <div key={slideIdx === active ? slideKey : slide.id} className="HomeV10-convoMessages">
              {slide.messages.map((m, idx) => (
                <div key={idx} className="HomeV10-msg">
                  <div
                    className={`HomeV10-msgAv HomeV10-msgAv${
                      m.variant === 'codi' ? 'Codi' : m.variant === 'user2' ? 'User2' : 'User1'
                    }`}
                  >
                    {m.variant === 'codi' ? (
                      <CodiSlackMark className="HomeV10-msgAvCodiMark" aria-hidden focusable="false" />
                    ) : (
                      m.initial
                    )}
                  </div>
                  <div>
                    <Layout flex align="center" gap={8} marginBottom={4}>
                      <Text size="body-md" semibold color="gray-900">
                        {m.name}
                      </Text>
                      {m.showAppTag ? (
                        <Text
                          size="body-xs"
                          color="gray-500"
                          __style={{
                            fontSize: 10,
                            fontWeight: 600,
                            background: '#eef2f6',
                            padding: '2px 6px',
                            borderRadius: 4
                          }}
                        >
                          APP
                        </Text>
                      ) : null}
                      <Text size="body-xs" color="gray-500">
                        {m.time}
                      </Text>
                    </Layout>
                    <Text size="body-sm" color="gray-700" __style={{ lineHeight: 1.5 }}>
                      {m.body}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="HomeV10-convoDots">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`HomeV10-convoDot${i === active ? ' is-active' : ''}`}
            aria-label={`Show conversation ${i + 1}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </>
  );
};

export default HeroConvoSlides;
